import React, { useState } from 'react';
import axios from 'axios';
import { X, Send, Loader2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  contactName: '',
  email: '',
  role: '',
  companyName: '',
  country: '',
  sector: '',
  situationType: '',
  capitalNeeded: '',
  urgency: '',
  description: ''
};

const AfricaSituationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetAndClose = () => {
    setSubmitted(false);
    setErrorMsg('');
    setFormData(initialState);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg('');
    try {
      await axios.post(`${API}/contact/africa-situation`, formData);
      setSubmitted(true);
      setTimeout(resetAndClose, 4000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="africa-situation-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={resetAndClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#a83232] rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-serif text-xl font-bold text-[#1a365d]">Submit a Situation</h2>
              <p className="text-xs text-gray-500">Berkshire Africa Special Situations</p>
            </div>
          </div>
          <button onClick={resetAndClose} className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="africa-situation-close-btn">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-12" data-testid="africa-situation-success">
              <div className="w-16 h-16 bg-[#e8f5f3] rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-[#2f9e8e]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1a365d] mb-2">Submission Received</h3>
              <p className="text-gray-600">Our restructuring team will review and respond within 5 business days.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="africa-situation-form">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-900 text-sm">
                  Bring us a situation requiring rescue financing, restructuring, distressed sale, bank recapitalization, or strategic asset divestment.
                </p>
              </div>

              {errorMsg && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{errorMsg}</div>
              )}

              <div className="space-y-4">
                <h3 className="font-semibold text-[#1a365d] border-b border-gray-200 pb-2">Contact</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Contact Name *</label>
                    <input type="text" name="contactName" required value={formData.contactName} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                      placeholder="John Smith" data-testid="africa-situation-input-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                      placeholder="john@company.com" data-testid="africa-situation-input-email" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Role / Capacity</label>
                  <input type="text" name="role" value={formData.role} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                    placeholder="CFO, Advisor, Board Member, Creditor, Shareholder..." data-testid="africa-situation-input-role" />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-[#1a365d] border-b border-gray-200 pb-2">Asset / Company</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Company / Asset Name *</label>
                    <input type="text" name="companyName" required value={formData.companyName} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                      placeholder="Company name" data-testid="africa-situation-input-company" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Country *</label>
                    <select name="country" required value={formData.country} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                      data-testid="africa-situation-input-country">
                      <option value="">Select country</option>
                      <option value="Kenya">Kenya</option>
                      <option value="South Africa">South Africa</option>
                      <option value="Ghana">Ghana</option>
                      <option value="Egypt">Egypt</option>
                      <option value="Nigeria">Nigeria</option>
                      <option value="Zambia">Zambia</option>
                      <option value="Angola">Angola</option>
                      <option value="Ethiopia">Ethiopia</option>
                      <option value="Mozambique">Mozambique</option>
                      <option value="Tanzania">Tanzania</option>
                      <option value="Other Africa">Other (Africa)</option>
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Sector *</label>
                    <select name="sector" required value={formData.sector} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                      data-testid="africa-situation-input-sector">
                      <option value="">Select sector</option>
                      <option value="Banking & Financial Services">Banking & Financial Services</option>
                      <option value="Power & Energy">Power & Energy</option>
                      <option value="Agro-Industrial">Agro-Industrial</option>
                      <option value="Logistics & Ports">Logistics & Ports</option>
                      <option value="Telecom & Fiber">Telecom & Fiber</option>
                      <option value="Mining & Resources">Mining & Resources</option>
                      <option value="Industrial">Industrial</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Situation Type *</label>
                    <select name="situationType" required value={formData.situationType} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                      data-testid="africa-situation-input-type">
                      <option value="">Select type</option>
                      <option value="Rescue Financing">Rescue Financing</option>
                      <option value="Restructuring / Liability Management">Restructuring / Liability Management</option>
                      <option value="Distressed Sale">Distressed Sale</option>
                      <option value="Bank Recapitalization">Bank Recapitalization</option>
                      <option value="Strategic Asset Divestment">Strategic Asset Divestment</option>
                      <option value="Bridge / Working Capital">Bridge / Working Capital</option>
                      <option value="Trade Finance">Trade Finance</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-[#1a365d] border-b border-gray-200 pb-2">Capital & Urgency</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Capital Required</label>
                    <select name="capitalNeeded" value={formData.capitalNeeded} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                      data-testid="africa-situation-input-capital">
                      <option value="">Select range</option>
                      <option value="< $5M">&lt; $5M</option>
                      <option value="$5M – $15M">$5M – $15M</option>
                      <option value="$15M – $50M">$15M – $50M</option>
                      <option value="$50M+">$50M+</option>
                      <option value="To be determined">To be determined</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Urgency</label>
                    <select name="urgency" value={formData.urgency} onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                      data-testid="africa-situation-input-urgency">
                      <option value="">Select</option>
                      <option value="Critical (< 30 days)">Critical (&lt; 30 days)</option>
                      <option value="Near-term (1–3 months)">Near-term (1–3 months)</option>
                      <option value="Medium-term (3–6 months)">Medium-term (3–6 months)</option>
                      <option value="Exploratory">Exploratory</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Situation Description *</label>
                <textarea name="description" required rows={5} value={formData.description} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e] resize-none"
                  placeholder="Background, capital structure, current pressure points, available collateral, ideal outcome..."
                  data-testid="africa-situation-input-description" />
              </div>

              <button type="submit" disabled={isSubmitting}
                className="w-full bg-[#a83232] hover:bg-[#8b2828] text-white py-3.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-70"
                data-testid="africa-situation-submit-btn">
                {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>) : (<><Send className="w-5 h-5" /> Submit Situation</>)}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AfricaSituationModal;
